import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Mirrors backend services/creative_engine/abh_chat_service.py state machine
// (rebuild): BRAINSTORM → HOOKS → BODY → CTAS → BRIEF → SENT.
const STAGE = { BRAINSTORM: 'BRAINSTORM', HOOKS: 'HOOKS', BODY: 'BODY', CTAS: 'CTAS', BRIEF: 'BRIEF', SENT: 'SENT' };
const STAGE_FLOW = [
  { id: STAGE.BRAINSTORM, label: 'Brainstorm' },
  { id: STAGE.HOOKS, label: 'Hooks' },
  { id: STAGE.BODY, label: 'Body' },
  { id: STAGE.CTAS, label: 'CTAs' },
  { id: STAGE.BRIEF, label: 'Brief' },
  { id: STAGE.SENT, label: 'Sent' },
];
const rank = (s) => STAGE_FLOW.findIndex((x) => x.id === s);
// Lock config per slot-producing stage.
const LOCK = {
  [STAGE.HOOKS]: { path: 'lock-hook', label: 'Lock Hook', section: 'hooks', another: 'hook', next: 'Body' },
  [STAGE.BODY]: { path: 'lock-body', label: 'Lock Body', section: 'body', next: 'CTAs' },
  [STAGE.CTAS]: { path: 'lock-cta', label: 'Lock CTA', section: 'ctas', another: 'CTA', next: 'Brief' },
};

// ── Stage progression indicator (existing pill chrome) ──
const StageIndicator = ({ stage }) => {
  const here = rank(stage);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="abh-ws-stage-indicator">
      {STAGE_FLOW.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`px-3 py-1 rounded-full text-xs ${
              stage === s.id ? 'bg-amber-500 text-white' : here > i ? 'bg-teal-500/10 text-teal-400' : 'bg-slate-800 text-slate-500'
            }`}
            data-testid={`abh-ws-stage-${s.id}`}
          >
            {s.label}
          </div>
          {i < STAGE_FLOW.length - 1 && <div className="w-3 h-px bg-slate-700"></div>}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Compliance flag rendering (reused shape) ──
const Severity = ({ s }) => {
  const sev = (s || 'MEDIUM').toUpperCase();
  const cls = sev === 'HIGH' ? 'bg-rose-500/20 text-rose-300' : sev === 'LOW' ? 'bg-slate-600/40 text-slate-300' : 'bg-amber-500/20 text-amber-300';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{sev}</span>;
};
const FlagRow = ({ f, body }) => (
  <div className="text-xs py-1 border-t border-slate-700/50 first:border-0">
    <div className="flex items-center gap-1.5">
      <Severity s={f.severity} />
      <span className="text-slate-500">{body || f.regulatory_body || f.rule_section || ''}</span>
      <span className="text-slate-300">{f.line_ref}</span>
    </div>
    {f.flagged_text && <div className="text-slate-300 mt-0.5">“{f.flagged_text}”</div>}
    {f.reason && <div className="text-slate-500 mt-0.5">{f.reason}</div>}
    {f.rewrite && <div className="text-teal-400 mt-0.5">→ {f.rewrite}</div>}
  </div>
);

// ── A single locked slot (editable inline; can be sent back to chat) ──
const Slot = ({ el, onEdit, onReopen, busy }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(el.content || '');
  useEffect(() => { setDraft(el.content || ''); }, [el.content]);
  return (
    <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg px-3 py-2" data-testid={`abh-ws-slot-${el.element_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="text-teal-400 text-xs font-semibold">{el.element_number}</span>
          {el.human_edited && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/40 text-slate-300" title="A human edited this slot">edited</span>
          )}
          {el.generated_by === 'OPERATOR' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300" title="Human-authored">manual</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <button onClick={() => setEditing(true)} disabled={busy}
              className="text-slate-400 hover:text-teal-300 text-xs disabled:opacity-50" data-testid={`abh-ws-slot-edit-${el.element_id}`}>
              Edit
            </button>
          )}
          <button onClick={() => onReopen(el.element_id)} disabled={busy}
            className="text-slate-400 hover:text-amber-300 text-xs disabled:opacity-50"
            title="Send this slot back to chat for another round" data-testid={`abh-ws-slot-reopen-${el.element_id}`}>
            ↩ Send to chat
          </button>
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white focus:border-teal-500 focus:outline-none resize-none"
            data-testid={`abh-ws-slot-textarea-${el.element_id}`}
          />
          <div className="flex gap-2">
            <button
              onClick={async () => { await onEdit(el.element_id, draft); setEditing(false); }}
              disabled={busy || !draft.trim()}
              className="px-2.5 py-1 bg-teal-500 text-white rounded text-xs hover:bg-teal-600 disabled:opacity-50">
              Save
            </button>
            <button onClick={() => { setDraft(el.content || ''); setEditing(false); }}
              className="px-2.5 py-1 bg-slate-700 text-slate-300 rounded text-xs hover:bg-slate-600">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-slate-200 text-sm whitespace-pre-wrap">{el.content}</div>
      )}
    </div>
  );
};

// ── A workspace section (Hooks row / Body single / CTAs row) ──
const SlotSection = ({ title, hint, items, onEdit, onReopen, busy, emptyHint, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-white text-sm font-medium">{title} <span className="text-slate-500 text-xs">({items.length})</span></h3>
      {hint && <span className="text-slate-500 text-xs">{hint}</span>}
    </div>
    {items.length === 0 ? (
      <div className="text-slate-500 text-xs bg-slate-800/40 border border-dashed border-slate-700 rounded-lg px-3 py-3">{emptyHint}</div>
    ) : (
      <div className="space-y-2">
        {items.map((el) => <Slot key={el.element_id} el={el} onEdit={onEdit} onReopen={onReopen} busy={busy} />)}
      </div>
    )}
  </div>
);

// ── One chat bubble ──
const Bubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[88%] rounded-lg px-3 py-2 text-sm ${
          isUser ? 'bg-teal-500/10 border border-teal-500/30 text-teal-100' : 'bg-slate-700/50 border border-slate-600 text-slate-200'
        }`}
        data-testid={`abh-ws-msg-${msg.msg_id || msg.role}`}
      >
        <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
        {!isUser && (msg.compliance_rewritten || msg.claim_cap_applied) && (
          <div className="text-[10px] text-slate-500 mt-1" title="Adjusted for ABH compliance before display.">adjusted for compliance</div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ABH Custom Creative Session — two-column workspace + persistent chat.
// Left: the assembled creative (hooks / body / CTAs / instructions / metadata).
// Right: the persistent chat where all brainstorm + hook/body/CTA iteration
// happens. Every slot is populated only by an explicit human lock.
// ============================================================================
const ABHCustomChatSession = ({ session, onBack }) => {
  const sessionId = session.session_id;

  const [seed, setSeed] = useState(null);
  const [stage, setStage] = useState(STAGE.BRAINSTORM);
  const [slots, setSlots] = useState({ hooks: [], body: null, ctas: [] });
  const [brief, setBrief] = useState({ instructions: '', traffic_channel: '', length: '', video_format: '' });
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);            // lock/advance/edit/reopen/brief
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);      // chat turn in flight
  const [afterLock, setAfterLock] = useState(null);   // { kind, next, label } → "Generate another? Y/N"
  const [briefSaved, setBriefSaved] = useState(false);
  const [sendOutcome, setSendOutcome] = useState(null);  // send response (fail flags or routed)
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const base = `${API}/abh/sessions/${sessionId}`;

  // GET /workspace is the canonical hydration source for slots/brief/stage.
  const refreshWorkspace = useCallback(async () => {
    const res = await axios.get(`${base}/workspace`);
    const w = res.data || {};
    setStage(w.stage || STAGE.BRAINSTORM);
    setSlots(w.slots || { hooks: [], body: null, ctas: [] });
    if (w.brief) setBrief((b) => ({ ...b, ...w.brief, instructions: w.brief.instructions ?? b.instructions }));
    return w;
  }, [base]);

  const refreshMessages = useCallback(async () => {
    const res = await axios.get(`${base}/messages`);
    setTranscript(res.data?.transcript || []);
  }, [base]);

  // Initial load: ensure the session record exists + grab seed context (start),
  // then hydrate full state from GET workspace + messages.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const startRes = await axios.post(`${base}/custom/start`);
        if (!alive) return;
        setSeed(startRes.data?.seed_context || null);
        await refreshWorkspace();
        await refreshMessages();
      } catch (err) {
        if (alive) setError(err.response?.data?.detail || 'Failed to start custom session');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [base, refreshWorkspace, refreshMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [transcript.length, sending]);

  const lockCfg = LOCK[stage];
  const chatEnabled = stage !== STAGE.SENT;

  // ── Chat (persistent; turn is tagged at the current stage server-side) ──
  const handleChat = async () => {
    if (!input.trim() || sending || !chatEnabled) return;
    const message = input.trim();
    setInput('');
    setSending(true);
    setError('');
    setTranscript((prev) => [...prev, { stage, role: 'user', content: message, msg_id: `temp_${Date.now()}` }]);
    try {
      const res = await axios.post(`${base}/chat`, { message });
      setTranscript((prev) => [...prev, {
        stage: res.data.stage, role: 'assistant', content: res.data.response,
        compliance_rewritten: res.data.compliance_rewritten, claim_cap_applied: res.data.claim_cap_applied,
        msg_id: res.data.message_id,
      }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Chat failed');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } };

  // ── Lock the latest chat draft into the next slot of the current section ──
  const handleLock = async () => {
    if (!lockCfg || busy) return;
    setBusy(true); setError(''); setBriefSaved(false);
    try {
      const res = await axios.post(`${base}/${lockCfg.path}`, {});
      await refreshWorkspace();
      if (lockCfg.another) {
        // Hooks / CTAs: offer "Generate another? Y/N".
        setAfterLock({ kind: lockCfg.another, next: lockCfg.next, prompt: res.data?.prompt });
      } else {
        // Body: exactly one — offer to advance.
        setAfterLock({ kind: null, next: lockCfg.next, prompt: res.data?.prompt });
      }
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${lockCfg.label}`);
    } finally {
      setBusy(false);
    }
  };

  // ── Advance (the "N — don't generate another" choice; also leaves brainstorm) ──
  const handleAdvance = async () => {
    if (busy) return;
    setBusy(true); setError(''); setAfterLock(null);
    try {
      await axios.post(`${base}/advance`, {});
      await refreshWorkspace();
      await refreshMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Advance failed');
    } finally {
      setBusy(false);
    }
  };

  const handleEditSlot = async (elementId, content) => {
    setBusy(true); setError('');
    try {
      await axios.patch(`${base}/slots/${elementId}`, { content });
      await refreshWorkspace();
    } catch (err) {
      setError(err.response?.data?.detail || 'Edit failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReopenSlot = async (elementId) => {
    if (!window.confirm('Send this slot back to chat for another round? It will be removed from the workspace.')) return;
    setBusy(true); setError(''); setAfterLock(null);
    try {
      await axios.post(`${base}/slots/reopen`, { element_id: elementId });
      await refreshWorkspace();
      await refreshMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Reopen failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveBrief = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${base}/brief`, {
        instructions: brief.instructions || '',
        traffic_channel: brief.traffic_channel || null,
        length: brief.length || null,
        video_format: brief.video_format || null,
      });
      await refreshWorkspace();
      setBriefSaved(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save brief');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    setBusy(true); setError(''); setSendOutcome(null);
    try {
      const res = await axios.post(`${base}/send`, {});
      setSendOutcome(res.data);
      await refreshWorkspace();   // stage → SENT on pass
    } catch (err) {
      setError(err.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="abh-ws-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
        <p className="text-slate-400 text-sm mt-3">Loading workspace…</p>
      </div>
    );
  }

  const stageLabel = (STAGE_FLOW.find((s) => s.id === stage) || {}).label || stage;
  const sendFailed = sendOutcome?.status === 'compliance_failed';
  const sendRouted = sendOutcome?.status === 'routed';

  return (
    <div className="space-y-4" data-testid="abh-ws">
      <StageIndicator stage={stage} />
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 px-3 py-2 rounded-lg text-sm" data-testid="abh-ws-error">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── LEFT: workspace (~2/3) ── */}
        <div className="lg:col-span-2 space-y-5" data-testid="abh-ws-left">
          <SlotSection
            title="Hooks" testid="abh-ws-hooks"
            hint={stage === STAGE.HOOKS ? 'Lock from chat →' : null}
            items={slots.hooks || []} onEdit={handleEditSlot} onReopen={handleReopenSlot} busy={busy}
            emptyHint="No hooks locked yet. Draft one in the chat, then lock it into H1."
          />
          <SlotSection
            title="Body (B1)" testid="abh-ws-body"
            hint={stage === STAGE.BODY ? 'Lock from chat →' : null}
            items={slots.body ? [slots.body] : []} onEdit={handleEditSlot} onReopen={handleReopenSlot} busy={busy}
            emptyHint="No body locked yet. Exactly one body (B1) is locked from the chat."
          />
          <SlotSection
            title="CTAs" testid="abh-ws-ctas"
            hint={stage === STAGE.CTAS ? 'Lock from chat →' : null}
            items={slots.ctas || []} onEdit={handleEditSlot} onReopen={handleReopenSlot} busy={busy}
            emptyHint="No CTAs locked yet. Draft one in the chat, then lock it into C1."
          />

          {/* Instructions */}
          <div data-testid="abh-ws-instructions">
            <h3 className="text-white text-sm font-medium mb-2">Instructions</h3>
            <textarea
              value={brief.instructions || ''}
              onChange={(e) => { setBrief((b) => ({ ...b, instructions: e.target.value })); setBriefSaved(false); }}
              rows={3} placeholder="Production instructions for JC…"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none resize-none"
              data-testid="abh-ws-instructions-input"
            />
          </div>

          {/* Brief metadata */}
          <div data-testid="abh-ws-meta">
            <h3 className="text-white text-sm font-medium mb-2">Brief metadata</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Traffic channel</label>
                <input type="text" value={brief.traffic_channel || ''}
                  onChange={(e) => { setBrief((b) => ({ ...b, traffic_channel: e.target.value })); setBriefSaved(false); }}
                  placeholder="META" data-testid="abh-ws-meta-channel"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Length</label>
                <input type="text" value={brief.length || ''}
                  onChange={(e) => { setBrief((b) => ({ ...b, length: e.target.value })); setBriefSaved(false); }}
                  placeholder="30s" data-testid="abh-ws-meta-length"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Video format</label>
                <input type="text" value={brief.video_format || ''}
                  onChange={(e) => { setBrief((b) => ({ ...b, video_format: e.target.value })); setBriefSaved(false); }}
                  placeholder="UGC / talking-head…" data-testid="abh-ws-meta-format"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={handleSaveBrief} disabled={busy}
                className="px-3 py-1.5 bg-slate-700 text-slate-200 rounded-lg text-xs hover:bg-slate-600 disabled:opacity-50" data-testid="abh-ws-brief-save">
                Save brief
              </button>
              {briefSaved && <span className="text-teal-400 text-xs">Saved</span>}
            </div>
          </div>

          {/* Send */}
          <div className="pt-2 border-t border-slate-800" data-testid="abh-ws-send-zone">
            {stage !== STAGE.SENT ? (
              <>
                <button onClick={handleSend} disabled={busy || stage !== STAGE.BRIEF}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50"
                  title={stage !== STAGE.BRIEF ? 'Advance through Hooks → Body → CTAs → Brief first' : 'Compliance-audit and route to JC'}
                  data-testid="abh-ws-send">
                  {busy ? 'Sending…' : 'Compliance check & Send to JC'}
                </button>
                {stage !== STAGE.BRIEF && (
                  <span className="text-slate-500 text-xs ml-3">Reach the Brief stage to send.</span>
                )}
              </>
            ) : (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4" data-testid="abh-ws-sent">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <span className="text-teal-300 font-medium text-sm">Sent to JC</span>
                </div>
                <p className="text-slate-500 text-xs mt-1">The full set (all hooks, body, all CTAs) routed to production.</p>
              </div>
            )}

            {/* Compliance gate outcome */}
            {sendFailed && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mt-3" data-testid="abh-ws-send-flags">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-amber-300 font-medium text-sm">⚠ Compliance review — nothing routed</span>
                  <span className="text-slate-500 text-xs">{(sendOutcome.flags || []).length} compliance · {(sendOutcome.brand_flags || []).length} brand</span>
                </div>
                {sendOutcome.banner && <p className="text-amber-200/80 text-xs whitespace-pre-wrap mb-2">{sendOutcome.banner}</p>}
                {(sendOutcome.flags || []).map((f, i) => <FlagRow key={`c${i}`} f={f} />)}
                {(sendOutcome.brand_flags || []).map((f, i) => <FlagRow key={`b${i}`} f={f} body={f.rule_section} />)}
                <p className="text-slate-500 text-xs mt-2">Resolve in chat (send slots back), then Send again.</p>
              </div>
            )}
            {sendRouted && (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 mt-3" data-testid="abh-ws-send-routed">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-teal-300 font-medium text-sm">Routed to JC</span>
                  {sendOutcome.route_result?.creative_identifier && (
                    <span className="text-slate-300 text-xs font-mono">{sendOutcome.route_result.creative_identifier}</span>
                  )}
                </div>
                {sendOutcome.brief && (
                  <p className="text-slate-500 text-xs">
                    {(sendOutcome.brief.hooks || []).length} hooks × B1 × {(sendOutcome.brief.ctas || []).length} CTAs — uploaded to Drive, Telegram sent.
                  </p>
                )}
              </div>
            )}

            {onBack && stage === STAGE.SENT && (
              <button onClick={onBack} className="mt-3 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm" data-testid="abh-ws-done-back">
                Back to ABH Engine
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT: persistent chat (~1/3, full height) ── */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-4 bg-slate-800/50 border border-slate-700 rounded-xl flex flex-col h-[560px] lg:h-[calc(100vh-9rem)]" data-testid="abh-ws-chat">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                <span className="text-sm font-medium text-white">{stageLabel} chat</span>
              </div>
              <span className="text-xs text-slate-500">Sonnet · ABH</span>
            </div>

            {/* Seed grounding (collapsible) */}
            {seed && (
              <details className="border-b border-slate-700 flex-shrink-0">
                <summary className="cursor-pointer text-xs text-slate-400 px-4 py-2 hover:text-slate-200">Grounding (framing + psychology map)</summary>
                <div className="px-4 pb-3 space-y-2 text-xs">
                  {seed.direction_summary && <p className="text-slate-300 whitespace-pre-wrap">{seed.direction_summary}</p>}
                  {(seed.psychology_map || []).slice(0, 6).map((e, i) => (
                    <div key={i} className="text-slate-400"><span className="text-teal-400">{e.entry_point_type || ''}</span> — {e.statement || ''}</div>
                  ))}
                </div>
              </details>
            )}

            <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
              {transcript.length === 0 ? (
                <div className="flex items-center justify-center h-full text-center">
                  <p className="text-slate-500 text-sm max-w-xs">
                    {stage === STAGE.BRAINSTORM && 'Brainstorm the concept and angle here. When ready, start hooks.'}
                    {stage === STAGE.HOOKS && 'Draft hooks here. Lock the ones you want into H-slots.'}
                    {stage === STAGE.BODY && 'Draft the single body (B1) here, then lock it.'}
                    {stage === STAGE.CTAS && 'Draft CTAs here. Lock the ones you want into C-slots.'}
                    {stage === STAGE.BRIEF && 'Creative assembled. Finalize the brief on the left, then Send.'}
                    {stage === STAGE.SENT && 'Sent to JC.'}
                  </p>
                </div>
              ) : (
                transcript.map((m, i) => <Bubble key={m.msg_id || i} msg={m} />)
              )}
              {sending && <div className="flex justify-start"><div className="bg-slate-700/50 rounded-lg px-3 py-2 text-sm text-slate-400 animate-pulse">Thinking…</div></div>}
              <div ref={endRef} />
            </div>

            {/* Per-stage action bar (lock / generate-another / advance) */}
            <div className="px-3 py-2 border-t border-slate-700 flex-shrink-0 space-y-2">
              {afterLock ? (
                <div className="bg-slate-900/60 rounded-lg p-2" data-testid="abh-ws-another">
                  <div className="text-xs text-slate-300 mb-1.5">{afterLock.prompt || (afterLock.kind ? `Generate another ${afterLock.kind}?` : 'Locked.')}</div>
                  <div className="flex gap-2">
                    {afterLock.kind && (
                      <button onClick={() => setAfterLock(null)} disabled={busy}
                        className="px-3 py-1 bg-teal-500 text-white rounded text-xs hover:bg-teal-600 disabled:opacity-50" data-testid="abh-ws-another-yes">
                        Yes — another {afterLock.kind}
                      </button>
                    )}
                    <button onClick={handleAdvance} disabled={busy}
                      className="px-3 py-1 bg-slate-700 text-slate-200 rounded text-xs hover:bg-slate-600 disabled:opacity-50" data-testid="abh-ws-another-no">
                      No — advance to {afterLock.next}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {lockCfg && (
                    <button onClick={handleLock} disabled={busy}
                      className="px-3 py-1.5 bg-teal-500 text-white rounded-lg text-xs hover:bg-teal-600 disabled:opacity-50"
                      title="Lock the latest chat reply into the next slot" data-testid="abh-ws-lock">
                      {busy ? 'Locking…' : `🔒 ${lockCfg.label}`}
                    </button>
                  )}
                  {(stage === STAGE.BRAINSTORM || stage === STAGE.CTAS || stage === STAGE.BODY) && (
                    <button onClick={handleAdvance} disabled={busy}
                      className="px-3 py-1.5 bg-slate-700 text-slate-200 rounded-lg text-xs hover:bg-slate-600 disabled:opacity-50" data-testid="abh-ws-advance">
                      {stage === STAGE.BRAINSTORM ? 'Start hooks →' : `Advance →`}
                    </button>
                  )}
                </div>
              )}

              {/* Chat input */}
              <div className="flex gap-2">
                <input
                  ref={inputRef} type="text" value={input}
                  onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={chatEnabled ? `Message the ${stageLabel} chat…` : 'Session sent — chat closed'}
                  disabled={sending || !chatEnabled}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none disabled:opacity-50"
                  data-testid="abh-ws-chat-input"
                />
                <button onClick={handleChat} disabled={sending || !input.trim() || !chatEnabled}
                  className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-50" data-testid="abh-ws-chat-send">
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ABHCustomChatSession;
